import { apiClient } from "../api/apiClient.js";

// Thin fetch wrappers over the Prompt 120 Backoffice Fake Location Signal
// API (BackofficeFakeLocationController/FakeLocationService). Same base/auth
// convention as ../geofence/geofenceApi.js/../abnormal/abnormalApi.js -
// apiClient.get/post with no `base` override, the same Bearer-session-
// authenticated backend every other backoffice controller uses. This file
// never calls an external anti-fraud/geolocation service, never implies
// AI/ML, never evaluates a geofence, and never sends a real notification -
// every write here maps 1:1 to one of the three real P120 routes below.

const FAKE_LOCATION_PATH = "/backoffice/tracking/fake-location";

// Exactly the query param names BackofficeFakeLocationController.ListEvaluations
// declares, in the same order as the controller signature - anything else
// passed to listFakeLocationEvaluations is silently dropped rather than
// forwarded, so a typo'd key can never accidentally reach the backend as an
// unfiltered pass-through. Scope-isolated visibility always happens on the
// backend from the caller's own session, never from a value this file sends.
const EVALUATIONS_LIST_PARAM_KEYS = ["workerId", "riskLevel", "fromUtc", "toUtc", "page", "pageSize"];

/** Builds a query string with every empty/null/undefined value omitted entirely (never sent as `key=`), restricted to `paramKeys`. */
function buildQuery(paramKeys, params = {}) {
  const searchParams = new URLSearchParams();
  for (const key of paramKeys) {
    const value = params[key];
    if (value === null || value === undefined || value === "") {
      continue;
    }
    searchParams.set(key, String(value));
  }
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export function listFakeLocationEvaluations(params = {}) {
  return apiClient.get(`${FAKE_LOCATION_PATH}/evaluations${buildQuery(EVALUATIONS_LIST_PARAM_KEYS, params)}`);
}

// Detail fetch requires a real, non-empty evaluationId - this file never
// issues a request for a missing/blank id (the caller only calls this once
// a row/card is actually clicked). GET .../evaluations/{evaluationId} is a
// real, implemented P120 route (FakeLocationEvaluationDetailDto, backed by
// dbo.WmxFakeLocationEvaluations) - not reserved/omitted.
export function getFakeLocationEvaluationDetail(evaluationId) {
  if (!evaluationId) {
    throw new Error("getFakeLocationEvaluationDetail requires an evaluationId");
  }
  return apiClient.get(`${FAKE_LOCATION_PATH}/evaluations/${encodeURIComponent(evaluationId)}`);
}

// dryRun defaults true, matching FakeLocationEvaluateRequest.DryRun's own
// default (FakeLocationDtos.cs) - a caller must explicitly opt out of
// dryRun to ever create a real FakeLocationSuspected abnormal.
export function evaluateFakeLocation({ workerId, assignmentId, fromUtc, toUtc, dryRun = true, purpose } = {}) {
  const body = {
    workerId: workerId || null,
    fromUtc: fromUtc || null,
    toUtc: toUtc || null,
    dryRun: Boolean(dryRun),
    purpose: purpose || null,
  };
  if (assignmentId) {
    body.assignmentId = assignmentId;
  }
  return apiClient.post(`${FAKE_LOCATION_PATH}/evaluate`, body);
}
